import { ReactNode } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import FormInputPropsWithInputType, { FormItemType } from './types';
import Input, { InputProps } from '../../atom/Input';
import Label from '../../atom/Label';
import ErrorMessage from '../../atom/ErrorMessage';
import { cva, VariantProps } from 'class-variance-authority';
import { cn } from '@/src/utils';
import TextArea, { TextAreaProps } from '../../atom/TextArea';
import DatePicker, { DatePickerProps } from '../../atom/DatePicker';
import DateRangePicker, {
  DateRangePickerProps,
} from '../../atom/DateRangePicker';
import RadioGroup, { RadioProps } from '../../atom/RadioGroup';
import Checkbox, { CheckboxProps } from '../../atom/Checkbox';
import Select, { SelectProps } from '../../atom/Select';
import { twJoin } from 'tailwind-merge';
import SingleImageUpload, {
  SingleImageUploadProps,
} from '../../atom/SingleImageUpload';
import IconButton from '../../atom/Button/IconButton';
import Typo from '../../atom/Typo';
import RadixIcon from '../../atom/Icons/RadixIcon';

const formItemContainerVariants = cva('', {
  variants: {
    direction: {
      horizontal: 'flex flex-row items-baseline gap-2',
      vertical: 'flex flex-col gap-2',
    },
  },
  defaultVariants: {
    direction: 'horizontal',
  },
});

const formItemContainerCheckboxWithRadioVariants = cva('', {
  variants: {
    direction: {
      horizontal: 'flex flex-row gap-2',
      vertical: 'flex flex-col gap-2',
    },
  },
  defaultVariants: {
    direction: 'horizontal',
  },
});

const formItemLabelVariants = cva('', {
  variants: {
    size: {
      L: 'min-w-[208px]',
      M: 'min-w-[164px]',
      Full: 'w-full',
    },
  },
  defaultVariants: {
    size: 'Full',
  },
});

export interface FormItemProps<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
> {
  /**
   * @description "text" | "checkbox" | "radio" | "textArea" | "file" | "select" | "datePicker" | "dateRangePicker"
   */
  type: FormItemType;
  label?: ReactNode | string;
  labelSize?: 'S' | 'M';
  name: UseControllerProps<TFieldValues, TName>['name'];
  control: UseControllerProps<TFieldValues>['control'];
  direction?: VariantProps<typeof formItemContainerVariants>['direction'];
  size?: VariantProps<typeof formItemLabelVariants>['size'];
  description?: ReactNode | string;
  rules?: UseControllerProps['rules'];
  optionList?: {
    label: string | ReactNode;
    value: string;
    disabled?: boolean;
  }[];
  errorMessage?: string;
  isRequiredHidden?: boolean;
  formItemClassName?: string;
  hasShadow?: boolean;
}

function FormItem<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  type,
  label,
  labelSize = 'S',
  direction = 'horizontal',
  size = 'M',
  name,
  control,
  description,
  rules,
  inputProps,
  optionList,
  errorMessage,
  isRequiredHidden = false,
  formItemClassName,
  hasShadow = false,
}: FormItemProps<TFieldValues, TName> & FormInputPropsWithInputType) {
  const isRequired = rules?.required;

  const {
    formState: { errors },
  } = useController({
    name,
    control,
  });
  const renderFormItemLabel = () => {
    if (!label) {
      return;
    }

    if (typeof label === 'string') {
      const isHorizen = direction === 'horizontal';

      if (isHorizen && size === 'Full') {
        throw Error("Horizontal 타입은 'L'| 'M' 사이즈 만 사용가능합니다 ");
      }

      return (
        <Label
          required={!!isRequired && !isRequiredHidden}
          labelSize={labelSize}
          className={cn(
            formItemLabelVariants({ size: isHorizen ? size : 'Full' }),
          )}
        >
          {label}
        </Label>
      );
    }

    return label;
  };

  const renderDescription = () => {
    if (typeof description === 'string') {
      return (
        <figure className="flex gap-x-1">
          <RadixIcon
            size={16}
            color="gray5"
            name="InfoCircledIcon"
            className="flex flex-shrink-0"
          />
          <Typo
            typoType="b14r"
            typoColor="gray5"
            className=" whitespace-pre-wrap"
          >
            {description}
          </Typo>
        </figure>
      );
    }

    return description;
  };

  const renderFormItem = (param: {
    type: FormInputPropsWithInputType['type'];
  }) => {
    if (!control) {
      return;
    }

    const { type } = param;

    const formItemInputProps = inputProps as InputProps;
    const formItemCheckboxProps = inputProps as CheckboxProps;
    const formItemRadioProps = inputProps as RadioProps;
    const formItemTextAreaProps = inputProps as TextAreaProps;
    const formItemDatePickerProps = inputProps as DatePickerProps;
    const formItemDateRangePickerProps = inputProps as DateRangePickerProps;
    const formItemSingleImageProps = inputProps as SingleImageUploadProps;
    const formItemSelectProps = inputProps as SelectProps;

    switch (type) {
      case 'text':
        return (
          <Controller
            control={control as Control<FieldValues>}
            name={name}
            rules={rules}
            render={({ field }) => (
              <div
                className={cn(
                  formItemContainerVariants({ direction }),
                  formItemClassName,
                )}
              >
                {renderFormItemLabel()}

                <div className={cn('flex flex-1 flex-col gap-2')}>
                  <Input
                    containerClassName={`flex-1 ${hasShadow && 'shadow-input'}`}
                    placeholder={
                      formItemInputProps?.placeholder ?? 'placeholder'
                    }
                    onClear={() => {
                      field.onChange('');
                    }}
                    {...field}
                    {...formItemInputProps}
                  />

                  {errors[name] && errorMessage && (
                    <ErrorMessage>{errorMessage}</ErrorMessage>
                  )}
                  {description && renderDescription()}
                </div>
              </div>
            )}
          />
        );

      case 'checkbox':
        return (
          <Controller
            control={control as Control<FieldValues>}
            name={name}
            rules={rules}
            render={({ field }) => {
              return (
                <div
                  className={cn(
                    formItemContainerCheckboxWithRadioVariants({ direction }),
                    formItemClassName,
                  )}
                >
                  {renderFormItemLabel()}

                  <div
                    className={twJoin(
                      `${
                        errors[name] && errorMessage && 'gap-y-2'
                      } flex flex-col`,
                    )}
                  >
                    <div className="flex flex-1 gap-x-2">
                      {optionList?.map((item, idx) => {
                        return (
                          <div
                            className="flex items-center gap-x-2"
                            key={idx.toString()}
                          >
                            <Checkbox
                              id={`${item.value}-${item.label}`}
                              checked={field.value?.includes(item.value)}
                              onCheckedChange={checked => {
                                return checked
                                  ? field.onChange([...field.value, item.value])
                                  : field.onChange(
                                      field.value?.filter(
                                        (value: string) => value !== item.value,
                                      ),
                                    );
                              }}
                              disabled={item.disabled}
                              {...formItemCheckboxProps}
                            />
                            <Label
                              htmlFor={`${item.value}-${item.label}`}
                              className="text-b16r text-gray6"
                            >
                              {item.label}
                            </Label>
                          </div>
                        );
                      })}
                    </div>

                    <div>
                      {errors[name] && errorMessage && (
                        <ErrorMessage>{errorMessage}</ErrorMessage>
                      )}
                      {description && renderDescription()}
                    </div>
                  </div>
                </div>
              );
            }}
          />
        );

      case 'radioGroup':
        return (
          <Controller
            control={control as Control<FieldValues>}
            name={name}
            rules={rules}
            render={({ field }) => {
              return (
                <div
                  className={cn(
                    formItemContainerCheckboxWithRadioVariants({ direction }),
                    formItemClassName,
                  )}
                >
                  {renderFormItemLabel()}

                  <div className={cn('flex flex-1 flex-col gap-2')}>
                    <RadioGroup
                      optionList={optionList?.map(item => {
                        return {
                          ...item,
                          labelProps: { className: 'text-b16r text-gray6' },
                          optionProps: { disabled: item.disabled },
                        };
                      })}
                      rootProps={{
                        value: field.value,
                        onValueChange: e => {
                          field.onChange(e);
                        },
                      }}
                      {...field}
                      {...formItemRadioProps}
                    />

                    {errors[name] && errorMessage && (
                      <ErrorMessage>{errorMessage}</ErrorMessage>
                    )}
                    {description && renderDescription()}
                  </div>
                </div>
              );
            }}
          />
        );

      case 'select':
        if (!optionList) {
          return;
        }

        return (
          <Controller
            control={control as Control<FieldValues>}
            name={name}
            rules={rules}
            render={({ field }) => {
              return (
                <div
                  className={cn(
                    formItemContainerVariants({ direction }),
                    formItemClassName,
                  )}
                >
                  {renderFormItemLabel()}
                  <div className={cn('flex flex-1 flex-col gap-2')}>
                    <Select
                      option={[{ optionList }]}
                      rootProps={{
                        value: field.value || '',
                        onValueChange: e => field.onChange(e),
                      }}
                      {...field}
                      {...formItemSelectProps}
                      valueProps={{
                        placeholder:
                          formItemSelectProps?.valueProps?.placeholder ??
                          'Select',
                        ...formItemSelectProps?.valueProps,
                      }}
                    />

                    {errors[name] && errorMessage && (
                      <ErrorMessage>{errorMessage}</ErrorMessage>
                    )}
                    {description && renderDescription()}
                  </div>
                </div>
              );
            }}
          />
        );

      case 'textArea':
        return (
          <Controller
            control={control as Control<FieldValues>}
            name={name}
            rules={rules}
            render={({ field }) => (
              <div
                className={cn(
                  formItemContainerVariants({ direction }),
                  formItemClassName,
                )}
              >
                {renderFormItemLabel()}

                <div className={cn('flex flex-1 flex-col gap-2')}>
                  <TextArea
                    className="flex-1"
                    placeholder={
                      formItemTextAreaProps?.placeholder ?? 'placeholder'
                    }
                    {...field}
                    {...formItemTextAreaProps}
                  />

                  {errors[name] && errorMessage && (
                    <ErrorMessage>{errorMessage}</ErrorMessage>
                  )}
                  {description && renderDescription()}
                </div>
              </div>
            )}
          />
        );

      case 'datePicker':
        return (
          <Controller
            control={control as Control<FieldValues>}
            name={name}
            rules={rules}
            render={({ field }) => (
              <div
                className={cn(
                  formItemContainerVariants({ direction }),
                  formItemClassName,
                )}
              >
                {renderFormItemLabel()}

                <div className={cn('flex flex-1 flex-col gap-2')}>
                  <DatePicker {...field} {...formItemDatePickerProps} />

                  {errors[name] && errorMessage && (
                    <ErrorMessage>{errorMessage}</ErrorMessage>
                  )}
                  {description && renderDescription()}
                </div>
              </div>
            )}
          />
        );

      case 'dateRangePicker':
        return (
          <Controller
            control={control as Control<FieldValues>}
            name={name}
            rules={rules}
            render={({ field }) => (
              <div
                className={cn(
                  formItemContainerVariants({ direction }),
                  formItemClassName,
                )}
              >
                {renderFormItemLabel()}

                <div className={cn('flex flex-1 flex-col gap-2')}>
                  <DateRangePicker
                    {...field}
                    {...formItemDateRangePickerProps}
                  />

                  {errors[name] && errorMessage && (
                    <ErrorMessage>{errorMessage}</ErrorMessage>
                  )}
                  {description && renderDescription()}
                </div>
              </div>
            )}
          />
        );

      case 'singleImage':
        return (
          <Controller
            control={control as Control<FieldValues>}
            name={name}
            rules={rules}
            render={({ field }) => (
              <div
                className={cn(
                  formItemContainerCheckboxWithRadioVariants({ direction }),
                  formItemClassName,
                )}
              >
                {renderFormItemLabel()}

                <div className={cn('flex flex-1 flex-col gap-2')}>
                  <SingleImageUpload
                    {...field}
                    value={
                      field.value[0]?.mediaUrl
                        ? field.value[0]?.mediaUrl
                        : undefined
                    }
                    onChange={media => {
                      field.onChange([media]);
                    }}
                    topAccessary={({ handleEdit, handleRemove }) => {
                      return (
                        <div className="flex gap-x-1">
                          <IconButton
                            buttonSize="xSmall"
                            buttonColor="outline"
                            lucideIconProps={{ name: 'Pencil' }}
                            onClick={() => {
                              handleEdit();
                            }}
                          />
                          <IconButton
                            buttonSize="xSmall"
                            buttonColor="outline"
                            lucideIconProps={{ name: 'X' }}
                            onClick={() => {
                              handleRemove();
                              field.onChange([]);
                            }}
                          />
                        </div>
                      );
                    }}
                    {...formItemSingleImageProps}
                  />

                  {errors[name] && errorMessage && (
                    <ErrorMessage>{errorMessage}</ErrorMessage>
                  )}
                  {description && renderDescription()}
                </div>
              </div>
            )}
          />
        );
    }
  };

  return (
    <>
      {renderFormItem({
        type,
      })}
    </>
  );
}

export default FormItem;
